import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { css, styled } from '@mui/material/styles';
import { type JSX, type KeyboardEvent, type MouseEvent, useCallback, useMemo, useState } from 'react';

import { ExpandIcon } from './icons';

const StyledMenuItem = styled(MenuItem, { shouldForwardProp: prop => prop !== 'isDanger' })<{ isDanger?: boolean }>`
  min-width: 150px;

  ${({ isDanger, theme }) =>
    isDanger &&
    css`
      color: ${theme.palette.error.main};

      :hover {
        color: ${theme.palette.error.contrastText};
        background-color: ${theme.palette.error.main};
      }
    `}
`;

const StyledUserContainerDiv = styled('div')`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  outline: 0;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};

    & svg path {
      fill: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

const StyledInitialsDiv = styled('div')`
  align-items: center;
  background-color: ${({ theme }) => theme.palette.secondary.main};
  border-radius: 50%;
  color: ${({ theme }) => theme.palette.common.white};
  display: flex;
  font-size: 13px;
  font-weight: 600;
  height: 28px;
  justify-content: center;
  line-height: 28px;
  margin: ${({ theme }) => theme.spacing(1)};
  width: 28px;
`;

const StyledExpandIconContainerSpan = styled('span')`
  display: flex;
  margin-left: 10px;
`;

export interface UserOption {
  action: () => void;
  label: string;
  warn?: boolean;
  showDivider?: boolean;
}

export type UserPanelProps = {
  userName: string;
  options: UserOption[];
  hideUserName?: boolean;
};

export function UserPanel(props: UserPanelProps): JSX.Element {
  const { userName, options, hideUserName } = props;

  const [optionsAnchor, setOptionsAnchor] = useState<Element | undefined>();
  const closeMenu = useCallback(() => setOptionsAnchor(undefined), []);
  const onOptionsExpand = (event: MouseEvent | KeyboardEvent): void => setOptionsAnchor(event.currentTarget);
  const optionsItems = useMemo(
    () =>
      options.map(({ action, label, warn, showDivider }, key) => {
        const onClick = (): void => {
          action();
          closeMenu();
        };

        return (
          <StyledMenuItem onClick={onClick} key={key} isDanger={warn} data-test="user-menu-item" divider={showDivider}>
            {label}
          </StyledMenuItem>
        );
      }),
    [options, closeMenu]
  );

  const initials = userName
    .split(/ +/)
    .slice(0, 2)
    .map(name => name.charAt(0).toUpperCase())
    .join('');

  return (
    <>
      <StyledUserContainerDiv
        role="button"
        onClick={onOptionsExpand}
        onKeyPress={onOptionsExpand}
        tabIndex={0}
        data-test="self-user"
      >
        <StyledInitialsDiv data-test="initials">{initials}</StyledInitialsDiv>
        {!hideUserName && (
          <>
            {userName}
            <StyledExpandIconContainerSpan>
              <ExpandIcon />
            </StyledExpandIconContainerSpan>
          </>
        )}
      </StyledUserContainerDiv>
      <Menu
        open={!!optionsAnchor}
        anchorEl={optionsAnchor}
        onClose={closeMenu}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{
          list: { disablePadding: true },
        }}
      >
        {optionsItems}
      </Menu>
    </>
  );
}
